import type {ResultadoDeDeshacer} from '@cnc/contracts';
import {useState} from 'react';
import {Link} from 'react-router';
import {useBitacora, useDeshacer} from '../api/sesion';
import {AYUDA_BITACORA as A} from '../componentes/ayuda-general';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {tr} from '../i18n/idioma';
import {fechaHora, numero, textoOGuion} from '../utilidades/formato';

const clase = 'mt-1 w-full rounded-md border border-linea px-2 py-1.5 text-sm text-tinta';

// Bitacora: quien hizo que y cuando, con los valores anteriores y nuevos.
export function PaginaBitacora() {
  const [pagina, setPagina] = useState(1);
  const [usuario, setUsuario] = useState('');
  const [accion, setAccion] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [buscar, setBuscar] = useState('');
  const bitacora = useBitacora({
    pagina,
    porPagina: 50,
    usuario: usuario ? Number(usuario) : undefined,
    accion: accion || undefined,
    desde: desde || undefined,
    hasta: hasta || undefined,
    buscar: buscar.trim() || undefined,
  });
  const cambiar = (poner: () => void) => {
    poner();
    setPagina(1);
  };
  // Deshacer (Daniel, 14/10/2026): primero dice que vuelve a como estaba y pide confirmar.
  const deshacer = useDeshacer();
  const [aviso, setAviso] = useState<{id: number; r: ResultadoDeDeshacer} | null>(null);
  const [mensaje, setMensaje] = useState<{texto: string; error: boolean} | null>(null);
  const fallo = (error: unknown) =>
    setMensaje({
      texto: error instanceof Error ? error.message : tr('No se pudo deshacer.', 'Could not undo.'),
      error: true,
    });
  const revisar = async (id: number) => {
    setMensaje(null);
    try {
      setAviso({id, r: await deshacer.mutateAsync({id, modo: 'revisar'})});
    } catch (error) {
      fallo(error);
    }
  };
  const confirmar = async () => {
    if (!aviso) return;
    try {
      const r = await deshacer.mutateAsync({id: aviso.id, modo: 'deshacer'});
      setAviso(null);
      setMensaje(r.hecho ? {texto: r.lineas.join(' '), error: false} : {texto: r.motivo ?? '', error: true});
      void bitacora.refetch();
    } catch (error) {
      setAviso(null);
      fallo(error);
    }
  };

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold text-verde-oscuro">{tr('Bitácora', 'Audit log')}</h1>
        <p className="text-sm text-apagado">
          {tr(
            'Todo lo que cambia en la aplicación queda aquí: quién, cuándo, sobre qué y con qué valores. Solo se agrega; nunca se borra. Cualquier cambio en los datos se puede deshacer con "↶ Deshacer" (salvo lo de las cuentas de usuario), si nadie lo cambió después.',
            'Everything that changes in the application is recorded here: who, when, on what and with which values. It is append-only; nothing is ever deleted. Any data change can be undone with "↶ Undo" (except user accounts), if nobody changed it afterwards.',
          )}
        </p>
      </div>

      <div className="grid gap-3 rounded-lg border border-linea bg-white p-3 md:grid-cols-5">
        <label className="block text-xs text-apagado">
          {tr('Usuario', 'User')}
          <NotaDeAyuda texto={A.usuario} dentroDeEtiqueta />
          <select value={usuario} onChange={e => cambiar(() => setUsuario(e.target.value))} className={clase}>
            <option value="">{tr('Todos', 'All')}</option>
            {bitacora.data?.usuarios.map(u => (
              <option key={u.id} value={u.id}>
                {u.nombre}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-apagado">
          {tr('Acción', 'Action')}
          <NotaDeAyuda texto={A.accion} dentroDeEtiqueta />
          <select value={accion} onChange={e => cambiar(() => setAccion(e.target.value))} className={clase}>
            <option value="">{tr('Todas', 'All')}</option>
            {bitacora.data?.acciones.map(a => (
              <option key={a.valor} value={a.valor}>
                {a.etiqueta} ({numero(a.cantidad)})
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-apagado">
          {tr('Desde', 'From')}
          <NotaDeAyuda texto={A.desde} dentroDeEtiqueta />
          <input type="date" value={desde} onChange={e => cambiar(() => setDesde(e.target.value))} className={clase} />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Hasta', 'To')}
          <NotaDeAyuda texto={A.hasta} dentroDeEtiqueta />
          <input type="date" value={hasta} onChange={e => cambiar(() => setHasta(e.target.value))} className={clase} />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Buscar (código, correo, archivo…)', 'Search (code, email, file…)')}
          <NotaDeAyuda texto={A.buscar} dentroDeEtiqueta />
          <input
            type="search"
            value={buscar}
            onChange={e => cambiar(() => setBuscar(e.target.value))}
            className={clase}
          />
        </label>
      </div>

      {bitacora.isError && <p className="text-sm text-alerta">{bitacora.error.message}</p>}
      {mensaje && (
        <p role="status" className={`text-sm ${mensaje.error ? 'text-alerta' : 'text-verde-oscuro'}`}>
          {mensaje.texto}
        </p>
      )}
      {aviso && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/40 p-4">
          <section
            role="alertdialog"
            aria-modal="true"
            aria-label={tr('Deshacer un cambio', 'Undo a change')}
            className="w-full max-w-lg rounded-xl border border-linea bg-white p-5 shadow-2xl"
          >
            <h2 className="text-lg font-semibold text-verde-oscuro">↶ {tr('Deshacer un cambio', 'Undo a change')}</h2>
            {aviso.r.posible ? (
              <ul className="mt-3 list-disc space-y-1 pl-5 text-sm">
                {aviso.r.lineas.map(l => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-alerta">{aviso.r.motivo}</p>
            )}
            {aviso.r.posible && (
              <p className="mt-3 text-xs text-apagado">
                {tr(
                  'Queda en la bitácora como un cambio nuevo; lo de antes no se borra.',
                  'It is recorded in the audit log as a new change; nothing is erased.',
                )}
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setAviso(null)}
                disabled={deshacer.isPending}
                className="rounded-md border border-linea bg-white px-4 py-2 text-sm hover:bg-papel"
              >
                {aviso.r.posible ? tr('Cancelar', 'Cancel') : tr('Cerrar', 'Close')}
              </button>
              {aviso.r.posible && (
                <button
                  type="button"
                  onClick={() => void confirmar()}
                  disabled={deshacer.isPending}
                  className="rounded-md bg-verde-oscuro px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  {deshacer.isPending ? tr('Deshaciendo…', 'Undoing…') : tr('Sí, deshacer', 'Yes, undo')}
                </button>
              )}
            </div>
          </section>
        </div>
      )}
      <div
        className={`overflow-auto rounded-lg border border-linea bg-white ${bitacora.isFetching ? 'opacity-70' : ''}`}
      >
        <table className="w-full text-sm">
          <thead className="bg-papel">
            <tr className="text-left text-xs uppercase tracking-wide text-apagado">
              <th className="px-3 py-2 font-semibold">
                {tr('Fecha y hora', 'Date and time')}
                <NotaDeAyuda texto={A.fecha} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Quién', 'Who')}
                <NotaDeAyuda texto={A.quien} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Qué', 'What')}
                <NotaDeAyuda texto={A.que} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Sobre', 'On')}
                <NotaDeAyuda texto={A.sobre} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Cambios', 'Changes')}
                <NotaDeAyuda texto={A.cambios} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Deshacer', 'Undo')}
                <NotaDeAyuda texto={A.deshacer} />
              </th>
            </tr>
          </thead>
          <tbody>
            {bitacora.data?.elementos.map(e => (
              <tr key={e.id} className="border-t border-linea/70 align-top">
                <td className="px-3 py-1.5 whitespace-nowrap">{fechaHora(e.fecha)}</td>
                <td className="px-3 py-1.5">
                  {e.autor ?? <span className="text-apagado">{tr('Sistema', 'System')}</span>}
                </td>
                <td className="px-3 py-1.5">
                  {e.accionEtiqueta}
                  {e.origen && <span className="ml-1 text-xs text-apagado">· {e.origen}</span>}
                </td>
                <td className="px-3 py-1.5">
                  {e.entidad === 'export' && e.referencia ? (
                    <Link
                      to={`/seguimiento/${encodeURIComponent(e.referencia)}`}
                      className="text-verde hover:underline"
                    >
                      {e.referencia}
                    </Link>
                  ) : (
                    textoOGuion(e.referencia)
                  )}
                </td>
                <td className="px-3 py-1.5">
                  {e.cambios.length === 0 ? (
                    <span className="text-apagado">—</span>
                  ) : (
                    <ul className="grid gap-x-4 gap-y-0.5 lg:grid-cols-2">
                      {e.cambios.slice(0, 12).map(c => (
                        <li key={c.campo} className="flex flex-wrap gap-1">
                          <span className="text-apagado">{c.etiqueta}:</span>
                          <span className="line-through decoration-alerta/60">{c.antes ?? '—'}</span>
                          <span>→</span>
                          <span className="font-medium">{c.despues ?? '—'}</span>
                        </li>
                      ))}
                      {e.cambios.length > 12 && (
                        <li className="text-xs text-apagado">
                          {tr(`y ${numero(e.cambios.length - 12)} más`, `and ${numero(e.cambios.length - 12)} more`)}
                        </li>
                      )}
                    </ul>
                  )}
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap">
                  {e.deshechaEl ? (
                    <span className="text-xs text-apagado" data-prueba="deshecha">
                      {tr(`Deshecho el ${fechaHora(e.deshechaEl)}`, `Undone on ${fechaHora(e.deshechaEl)}`)}
                    </span>
                  ) : e.deshacible ? (
                    <button
                      type="button"
                      onClick={() => void revisar(e.id)}
                      disabled={deshacer.isPending}
                      className="rounded-md border border-linea bg-white px-2 py-0.5 text-xs hover:bg-papel disabled:opacity-50"
                      aria-label={tr(
                        `Deshacer ${e.accionEtiqueta} ${e.referencia ?? ''}`,
                        `Undo ${e.accionEtiqueta} ${e.referencia ?? ''}`,
                      )}
                    >
                      ↶ {tr('Deshacer', 'Undo')}
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
            {bitacora.data && bitacora.data.elementos.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-apagado">
                  {tr('No hay registros con esos filtros.', 'No records match those filters.')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {bitacora.data && bitacora.data.totalPaginas > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <span className="text-apagado">
            {tr(`${numero(bitacora.data.total)} registros`, `${numero(bitacora.data.total)} records`)}
          </span>
          <button
            type="button"
            onClick={() => setPagina(p => p - 1)}
            disabled={pagina <= 1}
            className="rounded-md border border-linea bg-white px-2.5 py-1 hover:bg-papel disabled:opacity-40"
          >
            ‹ {tr('Anterior', 'Previous')}
          </button>
          <span className="tabular-nums text-apagado">
            {numero(pagina)} / {numero(bitacora.data.totalPaginas)}
          </span>
          <button
            type="button"
            onClick={() => setPagina(p => p + 1)}
            disabled={pagina >= bitacora.data.totalPaginas}
            className="rounded-md border border-linea bg-white px-2.5 py-1 hover:bg-papel disabled:opacity-40"
          >
            {tr('Siguiente', 'Next')} ›
          </button>
        </div>
      )}
    </section>
  );
}
